import { useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import type { ChartPeriod } from "@/entities/home";
import { RobotAvatar } from "@/entities/robot";
import { DailyResultRow, StatusChip, useMyRobot } from "@/entities/user-robot";
import { useStopRobot } from "@/features/stop-robot";
import { ApiError, classify } from "@/shared/api";
import { formatMoney, formatPercent, moneySign } from "@/shared/lib";
import { useResultPulse } from "@/shared/telegram";
import { Button, Modal, useToast } from "@/shared/ui";
import { useWallet } from "@/entities/wallet";
import { EquityChart } from "@/widgets/equity-chart";
import { CapitalHistoryList } from "@/widgets/my-result";
import { TopupSheet } from "@/widgets/topup-sheet";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";

/** Высота строки истории — фиксирована, иначе виртуализатор не посчитает офсеты. */
const ROW_HEIGHT = 45;

/**
 * Деталь купленного робота. Экрана в Figma нет: каркас взят у
 * `Robots · Detail` (20:79), график — `EquityChart Range=90D`, таблица дней —
 * паттерн `Trade Data` (25:125) (DESIGN.md §5).
 *
 * Отклик (FE-14): экран открывают ради одной цифры — сколько робот принёс, —
 * и её знак приходит пальцу одиночным пульсом раньше, чем глаз дочитает цвет.
 *
 * `daily_results` отдаётся целиком и без пагинации: это СУТОЧНЫЕ итоги (бэкенд
 * складывает часы), у демо-пользователя их 180, а за год будет 365. Поэтому
 * список виртуализируется — иначе каждый ререндер экрана перерисовывал бы всю
 * историю. График, в отличие от списка, показывает часы.
 */
export function MyRobotDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const userRobotId = Number(id);

  const [confirming, setConfirming] = useState(false);
  const [toppingUp, setToppingUp] = useState(false);
  const [period, setPeriod] = useState<ChartPeriod>("90d");
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data, isPending, isError, isFetching, error, refetch } = useMyRobot(userRobotId, period);
  const stop = useStopRobot(userRobotId);
  const wallet = useWallet();

  // Хук стоит до ранних возвратов: порядок хуков обязан быть неизменным.
  useResultPulse(data ? moneySign(data.earned_total) : undefined);

  const results = data?.daily_results ?? [];
  const virtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  if (!Number.isFinite(userRobotId)) {
    return (
      <ScreenState tone="info" icon="questionMark" title={t("myRobots.notFound.title")} body={t("myRobots.notFound.body")} />
    );
  }

  if (isPending) return <ScreenSkeleton blocks={[112, 300, 240]} />;

  if (isError) {
    // Чужая покупка приходит как 404: бэкенд не подтверждает существование id.
    const notFound = error instanceof ApiError && error.code === "NOT_FOUND";
    return (
      <ScreenState
        tone={notFound ? "info" : "loss"}
        icon={notFound ? "questionMark" : "warningFill"}
        title={t(notFound ? "myRobots.notFound.title" : "states.error.title")}
        body={t(notFound ? "myRobots.notFound.body" : "states.error.body")}
        {...(notFound ? {} : { action: { label: t("states.error.action"), onClick: () => void refetch() } })}
      />
    );
  }

  const sign = moneySign(data.earned_total);
  const tone = sign < 0 ? "text-status-loss" : sign > 0 ? "text-status-profit" : "text-text-secondary";
  const active = data.status === "active";

  const confirmStop = () => {
    stop.mutate(undefined, {
      onSuccess: () => {
        setConfirming(false);
        toast.show({ tone: "loss", title: t("myRobots.stopped", { name: data.robot.name }) });
      },
      onError: (stopError) => {
        setConfirming(false);
        const reaction = classify(stopError);
        // Робот уже остановлен другой вкладкой — данные протухли, не ошибка.
        if (reaction.kind === "conflict") void refetch();
        toast.show({
          tone: "loss",
          title: reaction.kind === "conflict" ? reaction.message : t("states.error.title"),
        });
      },
    });
  };

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <header className="flex h-[42px] items-center gap-2">
        <button
          type="button"
          onClick={() => void navigate(-1)}
          data-haptic="nav"
          aria-label={t("common.back")}
          className="press text-h1 font-medium text-text-primary"
        >
          ‹
        </button>
        <h1 className="text-h2 font-semibold text-text-primary">{data.robot.name}</h1>
      </header>

      <section className="flex flex-col gap-3 rounded-[20px] border border-border-subtle bg-background-elevated p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-[9px]">
            <RobotAvatar robot={data.robot} size={28} className="mt-0.5" />
            <div className="flex flex-col gap-0.5">
              <span className="text-body font-semibold text-text-primary">{data.robot.name}</span>
              <span className="text-caption font-normal text-text-secondary">
                {t("myRobots.invested")} {formatMoney(data.invested_total, { suffix: "USDT" })}
              </span>
            </div>
          </div>
          <StatusChip status={data.status} />
        </div>

        <dl className="flex items-end justify-between">
          <div className="flex flex-col gap-1">
            <dt className="text-caption font-medium text-text-secondary">{t("myRobots.earned")}</dt>
            <dd className={`text-h3 font-semibold ${tone}`}>
              {formatMoney(data.earned_total, { sign: "always", suffix: "USDT" })}
            </dd>
          </div>
          <dd className={`text-label font-semibold ${tone}`}>
            {formatPercent(data.earned_pct, { sign: "always" })}
          </dd>
        </dl>

        {/*
          Удержанное показывается только когда оно есть. «Заработано» выше уже
          чистое — комиссия из него вычтена, — поэтому строка отвечает на вопрос
          «а сколько взяла платформа», а не добавляется к итогу.
        */}
        {moneySign(data.fee_total) > 0 ? (
          <div className="flex items-baseline justify-between text-caption">
            <span className="font-normal text-text-secondary">{t("myRobots.feeTotal")}</span>
            <span className="font-semibold text-text-primary">
              {formatMoney(data.fee_total, { suffix: "USDT" })}
            </span>
          </div>
        ) : null}
      </section>

      <EquityChart
        points={data.chart}
        total={data.earned_total}
        totalPct={data.earned_pct}
        period={data.chart_period}
        onPeriodChange={setPeriod}
        busy={isFetching}
      />

      <section className="flex flex-col gap-2 rounded-[20px] border border-border-subtle bg-background-elevated p-4">
        <h2 className="text-body font-semibold text-text-primary">{t("myRobots.history")}</h2>

        {results.length === 0 ? (
          <p className="py-6 text-center text-caption font-normal text-text-secondary">
            {t("myRobots.historyEmpty")}
          </p>
        ) : (
          // Каскада по строкам здесь нет намеренно: список виртуализован,
          // строки переиспользуются при прокрутке, и вход отрабатывал бы
          // заново на каждой. Проявляется окно целиком, один раз (FE-13).
          <div ref={scrollRef} className="animate-fade-in no-scrollbar max-h-[360px] overflow-y-auto">
            <div className="relative w-full" style={{ height: `${String(virtualizer.getTotalSize())}px` }}>
              {virtualizer.getVirtualItems().map((row) => {
                const item = results[row.index];
                if (!item) return null;
                return (
                  <div
                    key={row.key}
                    className="absolute top-0 left-0 w-full"
                    style={{ height: `${String(row.size)}px`, transform: `translateY(${String(row.start)}px)` }}
                  >
                    <DailyResultRow item={item} />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/*
        История капитала (ТЗ §20). Стоит ПОД графиком намеренно: график
        показывает только торговый результат, и внесённые средства обязаны
        читаться отдельным списком, а не искаться глазами на линии (§22).
      */}
      <CapitalHistoryList entries={data.capital_history} />

      {active && data.robot.topup_enabled ? (
        <Button size="cta" block onClick={() => setToppingUp(true)}>
          {t("myRobots.topup.action")}
        </Button>
      ) : null}

      {active ? (
        <Button size="cta" variant="danger" block onClick={() => setConfirming(true)}>
          {t("myRobots.stop")}
        </Button>
      ) : null}

      {toppingUp && wallet.data !== undefined ? (
        <TopupSheet
          robot={data}
          balance={wallet.data.balance}
          onClose={() => setToppingUp(false)}
        />
      ) : null}

      {confirming ? (
        <Modal
          tone="danger"
          icon="handPalm"
          title={t("myRobots.stopConfirm.title")}
          body={t("myRobots.stopConfirm.body", { name: data.robot.name })}
          onClose={() => setConfirming(false)}
          footer={
            <div className="flex flex-col gap-2">
              <Button size="cta" variant="danger" block loading={stop.isPending} onClick={confirmStop}>
                {t("myRobots.stopConfirm.action")}
              </Button>
              <Button size="cta" variant="ghost" block onClick={() => setConfirming(false)}>
                {t("common.cancel")}
              </Button>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
